import { BrandProtectedComponent } from '@components'

import { Button, Flex, notify, Space, Spacing } from '@billy/design-system'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { NotificationKeys } from '../../../../enums/notificationKeys'
import { useForm, useSegment } from '../../../../hooks'
import { useEInvoicingCreateKYC } from '../../hooks/useEInvoicingCreateKYC'
import { useEInvoicingSetup } from '../../hooks/useEInvoicingSetup'
import { EInvoicingGenericStep } from '../EInvoicingStep/elements/EInvoicingGenericStep'
import { EInvoicingProgressBar } from '../EInvoicingStep/elements/EInvoicingProgressBar'
import { EInvoicingStepContainer } from '../EInvoicingStep/elements/EInvoicingStepContainer'
import { AddressingLineFormat } from './components/AddressingLineSelector/types/frAddressingLinesFormats'
import { AutoCompanyInformationForm, EInvoicingKycFormType } from './components/AutoCompanyInformationForm'
import { ManualCompanyForm } from './components/ManualCompanyForm'
import { getCompanyInfoValidationSchema, getDefaultCompanyInfoValues, getErrorMessageKeyFromResponse } from './utils'
import { computeFRAddressingLineFromCompanyIDs } from './utils/computeFRAddressingLineFromCompanyIDs'

export const EInvoicingCompanyInfoStep = () => {
  const { t } = useTranslation()
  const { track } = useSegment()

  const { organization } = useUserOrganization()

  const { navigateToEInvoices, currentStep, goToNextStep, goToStep, legalUnitEstablishment, setKycAttemptId } =
    useEInvoicingSetup()
  const { user } = useCurrentUser()

  const { createKYC, isLoading: isCreateKYCLoading } = useEInvoicingCreateKYC({
    onError: ({ body: errorBody }) => {
      notify({
        id: NotificationKeys.EInvoiceKYCCompanyDetailsError,
        message: t(getErrorMessageKeyFromResponse(errorBody)),
        variant: 'error',
      })

      goToStep(currentStep)
    },
  })

  const [showFooterButtons, setShowFooterButtons] = useState(false)

  const { Form, FormItem, setValue, watch, reset } = useForm({
    validationSchema: getCompanyInfoValidationSchema(t),
    defaultValues: getDefaultCompanyInfoValues(legalUnitEstablishment),
    onSubmit: async (values) => {
      // if auto-selected, companyLegalUnitRegistrationNumber is the SIREN
      // for retrocomp (pre-selected values coming from API) and manual form
      // we take the first 9 characters from SIRET and use them as SIREN
      const addressingLine = computeFRAddressingLineFromCompanyIDs({
        siren: values.companyLegalUnitRegistrationNumber,
        siret: values.companyRegistrationNumber,
        addressingLineFormat: values.addressingLineFormat,
      })

      if (!addressingLine) {
        throw new Error('Cannot create KYC without addressing line')
      }

      const siren =
        values.companyLegalUnitRegistrationNumber?.replace(/\s/g, '') ??
        values.companyRegistrationNumber.replace(/\s/g, '').slice(0, 9)

      const response = await createKYC({
        ...values,
        addressingLine,
        legalUnitIdentifier: siren,
        scope: {
          customerInvoicesEnabled: true,
          supplierInvoicesEnabled: true,
          selfBilledCustomerInvoicesEnabled: true,
          selfBilledSupplierInvoicesEnabled: true,
          eReportingEnabled: false,
        },
        companyRegistrationNumber: values.companyRegistrationNumber.replace(/\s/g, ''),
        accountHolderCountryCode: 'FR',
        accountHolderEmail: user?.email ?? '',
      })
      setKycAttemptId(response.data.kycAttempt.kycAttemptId)
      track('xxx - e-invoicing: Submitted Company Details (Client)', {
        form: formType,
        addressingLineFormat: values.addressingLineFormat,
      })
      goToNextStep()
    },
  })

  const onAddressingLineFormatChange = (addressingLineFormat: AddressingLineFormat) => {
    setValue('addressingLineFormat', addressingLineFormat)
  }

  const values = watch()
  const formType: EInvoicingKycFormType = values.formType

  // Special state for metrics, allows tracking _how_ users are filling the form
  // Default state is auto-full in case user "comes back"
  const setFormType = useCallback(
    (newFormType: EInvoicingKycFormType) => {
      setValue('formType', newFormType)
    },
    [setValue],
  )

  const futureAddressingLine = useMemo(() => {
    const addressingLine = computeFRAddressingLineFromCompanyIDs({
      siren: values.companyLegalUnitRegistrationNumber,
      siret: values.companyRegistrationNumber,
      addressingLineFormat: values.addressingLineFormat,
    })

    return addressingLine ?? '-'
  }, [values.companyRegistrationNumber, values.companyLegalUnitRegistrationNumber, values.addressingLineFormat])

  const isFormEmpty = useMemo(() => {
    const hiddenFormFields = ['formType', 'addressingLineFormat']
    return Object.entries(values)
      .filter(([field]) => !hiddenFormFields.includes(field))
      .every(([, value]) => !value)
  }, [values])

  const footerButtons = useMemo(
    () => (
      <Flex flexDirection="column" gap={Spacing.S12}>
        <Space size="s24" />
        <Button loading={isCreateKYCLoading} type="submit" fullWidth>
          {t('e_invoice.kyc.step.continue')}
        </Button>
        <Button variant="text" fullWidth onClick={navigateToEInvoices}>
          {t('e_invoice.kyc.step.cancel')}
        </Button>
      </Flex>
    ),
    [isCreateKYCLoading, t, navigateToEInvoices],
  )

  useEffect(() => {
    if (isFormEmpty) {
      setShowFooterButtons(false)
      return
    }

    // Ensures the footer buttons are shown if we have prefilled data
    // This is necessary because the data is fetched async and by default the buttons aren't shown initially
    if (!isFormEmpty && !showFooterButtons) {
      setShowFooterButtons(true)
    }
  }, [isFormEmpty, showFooterButtons])

  useEffect(() => {
    if (currentStep === 'EMPTY_STATE') {
      goToNextStep()
    }
  }, [currentStep, goToNextStep])

  if (!organization) {
    return null
  }

  return (
    <EInvoicingStepContainer
      headerContent={<EInvoicingProgressBar title={t('e_invoice.kyc.step.company_details.progress.title')} />}
    >
      <EInvoicingGenericStep
        title={t('e_invoice.kyc.step.company_details.title')}
        bannerType={formType === 'manual' ? 'warning' : 'info'}
        bannerText={
          formType === 'manual'
            ? t('e_invoice.kyc.step.company_details.manual_details_hint')
            : t('e_invoice.kyc.step.company_details.subtitle')
        }
      >
        <BrandProtectedComponent brand="ageras">
          <Form>
            <AutoCompanyInformationForm
              addressingLine={futureAddressingLine}
              onAddressingLineFormatChange={onAddressingLineFormatChange}
              addressingLineFormat={values.addressingLineFormat}
              formItem={FormItem}
              isFormEmpty={isFormEmpty}
              values={values}
              reset={reset}
              setValue={setValue}
              onFormTypeSelected={(formType) => {
                setShowFooterButtons(true)
                setFormType(formType)
              }}
            />

            {showFooterButtons ? footerButtons : null}
          </Form>
        </BrandProtectedComponent>

        <BrandProtectedComponent brand="billy">
          <Form>
            <ManualCompanyForm addressingLineFormat={values.addressingLineFormat} formItem={FormItem} />

            {footerButtons}
          </Form>
        </BrandProtectedComponent>
      </EInvoicingGenericStep>
    </EInvoicingStepContainer>
  )
}
